// export const formatDate = (date) => {
//   const fecha = new Date(date);
//   const mes = fecha.getMonth();
//   const dia = fecha.getDate();
//   const year = fecha.getFullYear();
//   return `${dia}/${mes + 1}/${year}`;
// };
import { format, formatInTimeZone } from "date-fns-tz";
import { es } from "date-fns/locale";

/** ¿El string trae timezone explícita? (sufijo `Z` u offset `±HH:MM` / `±HHMM`). */
const hasExplicitTimezone = (value: string): boolean =>
  /(?:Z|[+-]\d{2}:?\d{2})$/.test(value.trim());

/** ¿Es una fecha de calendario sin hora? (`YYYY-MM-DD`). */
const isDateOnly = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(value.trim());

/**
 * Convierte cualquier fecha del backend a un instante absoluto (`Date`),
 * detectando su FORMA — así no dependemos de que el back sea consistente:
 *
 * 1. `Date` → ya es un instante absoluto, se usa tal cual.
 * 2. String con `Z`/offset (`...Z`, `+00:00`, `-03:00`) → instante real; JS lo
 *    parsea bien (createdAt/updatedAt serializados por trd-core REST, etc.).
 * 3. String datetime SIN timezone → lo que devuelve Hasura al leer columnas
 *    `timestamp without time zone` (ej. `"2026-05-23T06:06:55.825"`). El back
 *    guarda wall-clock UTC (la DB corre en GMT), así que lo interpretamos como
 *    UTC agregándole la `Z`.
 * 4. String date-only (`"1990-03-04"`, ej. birthDate) → fecha de calendario →
 *    medianoche LOCAL, sin correr el día.
 *
 * En los 4 casos `format()` lo muestra después en la TZ local del browser.
 */
const toInstant = (date: Date | string): Date => {
  if (date instanceof Date) return date;
  const s = String(date).trim();
  if (isDateOnly(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  if (hasExplicitTimezone(s)) return new Date(s);
  const naive = (s.includes("T") ? s : s.replace(" ", "T")).replace(/Z$/, "");
  return new Date(`${naive}Z`);
};

/**
 * Formatea una fecha del backend en la hora LOCAL del usuario.
 *
 * Detecta automáticamente si el valor viene con timezone (`Z`/offset), naive
 * (sin TZ, como devuelve Hasura) o como date-only, y hace lo correcto en cada
 * caso (ver `toInstant`). Un solo lugar resuelve TODOS los formatos → el
 * call-site nunca tiene que adivinar y queda blindado ante inconsistencias del
 * backend.
 *
 * @param date    ISO con `Z`/offset, datetime naive, date-only, o `Date`.
 * @param options patrón de date-fns. Default `"dd/MMMM/yyyy HH:mm'hs"`.
 *
 * @deprecated-param `_utc` — OBSOLETO, se ignora. Antes elegía entre dos
 * comportamientos (uno roto según el origen del dato); ahora la detección lo
 * vuelve innecesario. Se mantiene en la firma solo para no romper los ~213
 * call-sites que todavía lo pasan; se puede limpiar en un pase aparte.
 */
export const formatDate = (
  date: Date | string,
  options = "dd/MMMM/yyyy HH:mm'hs",
  _utc?: boolean
) => {
  void _utc; // OBSOLETO: se ignora a propósito (la detección lo vuelve innecesario)
  if (date === null || date === undefined || date === "") return "";
  return format(toInstant(date), options, {
    locale: es,
  });
};

/**
 * Formatea una fecha en la timezone del NEGOCIO, no en la del que mira.
 *
 * Para las fechas que son una **afirmación del negocio** y no un instante que
 * cada uno lee en su hora: el vencimiento de una venta es el caso claro. Ahí la
 * hora local del browser no sólo es irrelevante, es peligrosa.
 *
 * **Por qué importa, con el número concreto**: trd-core persiste un vencimiento
 * como el último instante de ese día en la zona del negocio
 * (`dayjs(instant).tz(tz).endOf('day')`, `Order.endOfDayIn`). Para un negocio
 * argentino, "vence el 10/09" se guarda como `2026-09-11T02:59:59.999Z`. Ese
 * instante cae el **11** en cualquier zona con offset ≥ 0 — UTC, toda Europa—,
 * así que el mismo comprobante impreso desde Madrid le daba al cliente un día
 * más de descuento por pronto pago que el impreso desde Buenos Aires.
 *
 * Se degrada a la hora local si el IANA es inválido, con el mismo criterio que
 * `Order.endOfDayIn` en trd-core: una zona mal escrita no puede tumbar la
 * impresión del comprobante.
 *
 * @param timezone nombre IANA (`business_configs[0].timezone`). Si es `null` o
 *                 `undefined` —un negocio que nunca lo configuró— se usa la
 *                 zona local, que es lo que se hacía antes de este parámetro.
 */
export const formatDateInTimeZone = (
  date: Date | string,
  timezone: string | null | undefined,
  options = "dd/MM/yyyy"
) => {
  if (date === null || date === undefined || date === "") return "";

  const instant = toInstant(date);

  if (!timezone) return format(instant, options, { locale: es });

  try {
    return formatInTimeZone(instant, timezone, options, { locale: es });
  } catch {
    return format(instant, options, { locale: es });
  }
};

/**
 * Convierte a `Date` tratando un datetime SIN timezone como hora **LOCAL**.
 *
 * Es la regla contraria a `toInstant`, y por eso vive aparte.
 */
const toWallClockInstant = (date: Date | string): Date => {
  if (date instanceof Date) return date;
  const s = String(date).trim();
  if (isDateOnly(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  // Con timezone explícita el valor ES un instante: se respeta.
  if (hasExplicitTimezone(s)) return new Date(s);
  // Sin `Z` y sin offset: es hora de pared. `new Date("2026-09-10T10:00:00")`
  // la interpreta como local, que es exactamente lo que queremos.
  return new Date(s.includes("T") ? s : s.replace(" ", "T"));
};

/**
 * Formatea una fecha que el backend guarda como **hora de pared local**, sin
 * correrla.
 *
 * # Por qué existe, y por qué NO alcanza con `formatDate`
 *
 * `formatDate` interpreta un datetime sin timezone como **UTC** (regla 3 de
 * `toInstant`) y después lo muestra en la zona del browser. Eso es correcto
 * para lo que documenta —`createdAt`, `updatedAt`: core los serializa en UTC—
 * y **falso para las reservas**.
 *
 * El bug que lo motivó: se reservaba el turno de las **10:00**, core guardaba
 * `2026-09-10T10:00:00` —naive, hora local, la que eligió el usuario— y el
 * detalle de la reserva mostraba **07:00**, tres horas antes. El calendario de
 * la misma pantalla la mostraba bien porque pasa el string crudo y JS parsea un
 * ISO sin zona como local. Dos vistas de la misma reserva, dos horas distintas.
 *
 * La convención está fijada del lado de core
 * (https://github.com/trudapp/trd-core/commit/edfaddb1): `startReservation` y
 * `endReservation` viajan sin `Z` ni offset y son wall-clock local. Si eso
 * cambia, core avisa antes, porque es un cambio de contrato.
 *
 * # Cuándo usar cada uno
 *
 * | El backend manda… | Usar |
 * | --- | --- |
 * | Un instante en UTC (`createdAt`, `updatedAt`) | `formatDate` |
 * | Hora de pared local (reservas) | `formatWallClock` |
 *
 * Ante la duda, mirá si el valor cambia de significado al mudarse de zona: la
 * hora de un turno no; el momento en que se creó un registro sí.
 */
export const formatWallClock = (
  date: Date | string | null | undefined,
  options = "dd/MMMM/yyyy HH:mm'hs"
) => {
  if (date === null || date === undefined || date === "") return "";
  return format(toWallClockInstant(date), options, { locale: es });
};

export const formatDateFromUTC = (date: Date | string | number) => {
  const fecha = new Date(date);
  const year = fecha.getFullYear();
  const month = fecha.getMonth();
  const day = fecha.getDate();
  const hour = fecha.getHours();
  const minute = fecha.getMinutes();
  const second = fecha.getSeconds();
  const millisecond = fecha.getMilliseconds();

  const fechaUtc = new Date(
    Date.UTC(year, month, day, hour, minute, second, millisecond)
  );

  return fechaUtc;
};

export const toLocaleISOString = (date: Date) => {
  return new Date(date.getTime() - date.getTimezoneOffset() * 1000 * 60)
    .toISOString()
    .replace("Z", "");
};

/**
 * "YYYY-MM-DDTHH:mm" (datetime-local input) → ISO UTC. Trata los componentes
 * como hora local del usuario. Más robusto que `new Date(local).toISOString()`,
 * cuyo parseo de strings sin offset es runtime-dependent. Ver
 * `feedback_dates_golden_rule` / docs/convenciones/primitivos-ui.md (R-DATES-INPUT).
 */
export function localDatetimeToISO(local: string): string {
  if (!local) return "";
  const [datePart, timePart] = local.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = (timePart ?? "00:00").split(":").map(Number);
  return new Date(y, m - 1, d, hh, mm, 0, 0).toISOString();
}

/**
 * "YYYY-MM-DD" (date input) → ISO UTC al midnight LOCAL del usuario.
 * Evita el bug de `new Date("YYYY-MM-DD").toISOString()` que parsea como UTC
 * midnight, lo cual shiftea el día en TZs negativas.
 */
export function localDateToISO(local: string): string {
  if (!local) return "";
  const [y, m, d] = local.split("-").map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

/**
 * "YYYY-MM-DD" → `Date` a medianoche LOCAL, para alimentar un `<DatePicker>`.
 * Inversa exacta de `formatLocalDate`. Ver R-DATES-PICKER.
 *
 * **NO usar `new Date("2019-01-01")`**: por spec de ECMAScript un string
 * date-only se parsea como **UTC**, así que en Argentina (UTC-3) ese instante
 * cae el 31/12/2018 a las 21:00 y el picker dibuja el día anterior. Una fecha
 * de calendario (nacimiento, vencimiento) no tiene hora ni timezone: nunca
 * tiene que convertirse en un instante.
 *
 * Devuelve `undefined` para vacío o basura, que es lo que espera `<DatePicker>`.
 */
export function parseLocalDate(value?: string | null): Date | undefined {
  if (!value) return undefined;
  const [y, m, d] = String(value).trim().slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return undefined;
  const date = new Date(y, m - 1, d, 0, 0, 0, 0);
  // Rechaza fechas imposibles ("2019-02-31" rebalsa a marzo).
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return undefined;
  }
  return date;
}

/**
 * `Date` (el que devuelve `<DatePicker>`) → "YYYY-MM-DD" leyendo los componentes
 * LOCALES. Inversa exacta de `parseLocalDate`. Ver R-DATES-PICKER.
 *
 * **NO usar `toISOString().slice(0, 10)`**: pasa por UTC y corre el día en las
 * TZs al este de Greenwich.
 */
export function formatLocalDate(date?: Date | null): string {
  if (!date || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** "YYYY-MM-DD" de hoy en hora local (NO `toISOString().slice(0,10)`). */
export function todayLocalISODate(): string {
  return formatLocalDate(new Date());
}

export const formatDateToBack = (dateIsoString: string) => {
  if (!dateIsoString) return null;

  return dateIsoString.split("T")[0];
};
